/* eslint-disable indent */
import { yupResolver } from '@hookform/resolvers/yup';
import { FC, useContext, useEffect, useMemo } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { Trans, useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { v4 as uuid } from 'uuid';
import * as yup from 'yup';

import { ButtonLink } from '@pulse-web-ui/button-link';
import { Checkbox } from '@pulse-web-ui/checkbox';
import { DocumentTransparent } from '@pulse-web-ui/icons';

import { PageBackButton, StyledButton } from '@src/common-components/button';
import {
  ArrowLeftIcon2,
  Container,
  DocumentsWrapper,
  OrderTitle,
  OrderWrapper,
  Skeleton,
} from '@src/components';
import { OrderWrapperTitle } from '@src/components/container';
import { sendAnalyticEvent } from '@src/components/web-analytic';
import {
  ClientDocumentType,
  FlatSteps,
  HouseSteps,
  INSURANCE_POLICIES,
  PAYMENT_ROUTE,
  Product,
  analyticEvents,
  iflFlatRoute,
  iflHouseRoute,
  iflRoute,
  miteRoute,
  nonSubscriptionProducts,
  nsRoute,
  petsRoute,
  sportNsRoute,
  televetPlusRoute,
} from '@src/constants';
import { AuthEndpoints } from '@src/constants/request-data';
import { GlobalErrorInfo } from '@src/features';
import { useHandlePressKey, useIsDesktop, useRequest } from '@src/hooks';
import { BaseLayout } from '@src/layouts/base-layout';
import { useOrderPrice, useSortedMiteDocuments } from '@src/pages/order/hooks';
import { OrderActionTypes, Store } from '@src/store';
import { KeyCode, OrderItemType, OrderItemValueType } from '@src/types';
import {
  handleAnalyticsEvents,
  scrollToIframe,
  sendFlatProgress,
  sendHouseProgress,
} from '@src/utils';

import { ClientDocumentItem } from './components/client-document-item';
import {
  DocumentsSubTitle,
  OrderCheckoutAggrWrapper,
  OrderCheckoutTitleWrapper,
} from './components/components.styles';
import { OrderCheckoutItem } from './components/order-checkout-item';
import { ContractDataType } from './order-types';

export const OrderCheckout: FC = () => {
  const { t } = useTranslation();
  const {
    state: {
      stateFormIFLFlat: { presetData: flatPresetData },
      stateFormIFLHouse: { presetData: housePresetData },
      stateFormPets: { presetData },
      stateUser: { profile, selectedProduct, preset },
      stateOrder: { orderSubmitData, orderInitData },
      stateAuth: { authTokens, authorizeRefRoute },
      stateWizard: { currentStep },
      stateUser,
    },
    dispatch,
  } = useContext(Store);
  const isDesktop = useIsDesktop();
  const orderPrice = useOrderPrice();
  const isTelevetPlusRoute = selectedProduct === Product.PETS_TELEVET_PLUS;
  const isMiteRoute = selectedProduct === Product.MITE;
  const schema = yup.object().shape({
    insurancePolicies: yup
      .bool()
      .oneOf([true], t('COMMON:errors.withoutAgreement') || ''),
  });
  const currentSelectedProduct = localStorage.getItem('selectedProduct');

  const emailDescription = useMemo(() => {
    switch (authorizeRefRoute) {
      case petsRoute:
      case televetPlusRoute:
        return t('ORDER:labels.policySentValidAfterPayment');
      case miteRoute:
        return t('MITE_FORM:labels.documentsSentEmail');
      default:
        return t('ORDER:labels.policySentToEmail');
    }
  }, [authorizeRefRoute]);

  useEffect(() => {
    switch (authorizeRefRoute) {
      case iflHouseRoute:
        if (preset) {
          sendAnalyticEvent(analyticEvents.iflHousePresetStep4Docs);
        } else {
          sendAnalyticEvent(analyticEvents.iflStep8Docs);
          sendAnalyticEvent(analyticEvents.iflHouseStep8Docs);
        }
        break;
      case iflFlatRoute:
        if (preset) {
          sendAnalyticEvent(analyticEvents.iflFlatPresetStep4Docs);
        } else {
          sendAnalyticEvent(analyticEvents.iflStep8Docs);
          sendAnalyticEvent(analyticEvents.iflFlatStep8Docs);
        }
        break;
      case petsRoute:
        const event = !!presetData
          ? analyticEvents.petToStepDocsPreset
          : analyticEvents.petToStepDocs;
        sendAnalyticEvent(event);
        break;
      case sportNsRoute:
        sendAnalyticEvent(analyticEvents.sportToStepDocs);
        break;
      case miteRoute:
        sendAnalyticEvent(analyticEvents.miteToStepDocs);
        break;
      case televetPlusRoute:
        sendAnalyticEvent(analyticEvents.petTelDocsOpen);
        break;
      default:
        break;
    }
  }, []);

  const navigate = useNavigate();

  const {
    control,
    formState: { errors },
    handleSubmit,
  } = useForm<{ insurancePolicies: boolean }>({
    resolver: yupResolver(schema),
    shouldFocusError: true,
    mode: 'all',
    defaultValues: {
      insurancePolicies: false,
    },
  });

  const sortedDocuments = isMiteRoute
    ? useSortedMiteDocuments(orderSubmitData?.clientDocument || [])
    : orderSubmitData?.clientDocument;

  const clientDocuments = sortedDocuments?.map((document) => ({
    ...document,
    icon: DocumentTransparent,
  }));

  const handleViewAgreement = () => {
    switch (authorizeRefRoute) {
      case iflRoute:
        sendAnalyticEvent(analyticEvents.viewAgreementRealty);
        break;
      case nsRoute:
        sendAnalyticEvent(analyticEvents.viewAgreementAccident);
        break;
    }
  };

  const acceptTermsLabel = useMemo(() => {
    const isKidIncluded = clientDocuments?.some(
      (doc) => doc.clientDocumentType === ClientDocumentType.KEY_INFO_DOCUMENT
    );

    const kidLabel = isKidIncluded ? t('ORDER:labels.kid') : '';
    const acceptTerms = t(
      'ORDER:labels.acceptTermsWithKeyInformationDocumentCondition',
      { kid: kidLabel }
    );
    const miteAcceptTerms = t('MITE_FORM:labels.insurancePolicyTerms', {
      kid: kidLabel,
    });

    const renderTrans = (terms: string) => (
      <Trans
        components={{
          a: (
            <ButtonLink
              route={INSURANCE_POLICIES}
              onClick={handleViewAgreement}
              target="_blank"
              variant="text"
              style={{ fontSize: 'inherit' }}
            />
          ),
        }}
      >
        {`${terms} ${t('ORDER:labels.nonAcceptanceAgreementLink')}`}
      </Trans>
    );

    const termsToRender =
      selectedProduct === Product.MITE ? miteAcceptTerms : acceptTerms;

    return selectedProduct === Product.SPORT
      ? acceptTerms
      : renderTrans(termsToRender);
  }, [selectedProduct, clientDocuments]);

  useEffect(() => {
    if (!orderSubmitData) {
      navigate(authorizeRefRoute ? authorizeRefRoute : '/'); // проверить
    }
  }, [orderSubmitData]);

  const orderResult: OrderItemType[] = [
    {
      type: OrderItemValueType.SIMPLE,
      title: t('ORDER:labels.policyNumber'),
      value: `${orderSubmitData?.contractNumber}`, // проверить - с бэка в моковых данных не приходит contractNumber, только orderNumber
    },
    {
      type: OrderItemValueType.SIMPLE,
      title: emailDescription,
      value: profile?.profile.email || '',
    },
  ];

  if (selectedProduct !== Product.SPORT) {
    orderResult.unshift(orderPrice);
  }

  const contractData: ContractDataType = {
    amount: orderSubmitData?.premiumAndDelta,
    contractId: orderSubmitData?.contractId,
    email: stateUser.profile?.profile?.email,
    operationType: 'ORDER',
    orderNumber: orderSubmitData?.orderNumber,
    paymentDocumentId: orderSubmitData?.billId,
    subscriptionId: orderSubmitData?.subscriptionId,
    subscriptionType: orderSubmitData?.subscriptionType,
  };

  const { isLoading, res, error, refetch } = useRequest(
    'formIFLSubscriptionSubmit',
    'post',
    AuthEndpoints.INIT_ORDER,
    contractData,
    [orderInitData, authTokens?.authorization?.accessToken],
    true,
    authTokens?.authorization?.accessToken
  );

  const handleClickBack = () => {
    switch (authorizeRefRoute) {
      case iflRoute:
        sendAnalyticEvent(analyticEvents.toPreviousRealty, {
          screen: currentStep + 2,
        });
        break;
      case nsRoute:
        sendAnalyticEvent(analyticEvents.toPreviousAccident, {
          screen: currentStep + 2,
        });
        break;
    }

    navigate('/order-detail');
  };

  const submitPage = handleSubmit(() => {
    refetch();
  });

  const handleKeyPressEnter = () => submitPage();
  useHandlePressKey(KeyCode.ENTER, handleKeyPressEnter);

  useEffect(() => {
    if (!isLoading && res) {
      dispatch({
        type: OrderActionTypes.SetOrderInitData,
        payload: res,
      });

      if (res?.signature) {
        let data;

        if (authorizeRefRoute === petsRoute) {
          data = presetData;
        }

        if (authorizeRefRoute === iflHouseRoute) {
          data = housePresetData;
        }

        if (authorizeRefRoute === iflFlatRoute) {
          data = flatPresetData;
        }

        handleAnalyticsEvents.handleNavigateToPaymentAnalyticsEvents({
          authorizeRefRoute,
          presetData: data,
        });

        navigate(PAYMENT_ROUTE);
      }
    }
  }, [isLoading, res]);

  useEffect(() => {
    switch (currentSelectedProduct) {
      case Product.APARTMENT:
        sendFlatProgress(FlatSteps.ORDER_PAYMENT);
        break;
      case Product.HOUSE:
        sendHouseProgress(HouseSteps.ORDER_PAYMENT);
        break;
    }
  }, []);

  const disabled = Boolean(Object.keys(errors).length);

  const Footer = useMemo(
    () => (
      <>
        <StyledButton
          label={t('ORDER:buttons.proceedToCheckout') || ''}
          variant="primary"
          onClick={submitPage}
          adaptiveWidth={!isDesktop}
          disabled={disabled}
        />
      </>
    ),
    [isLoading, isDesktop, disabled]
  );

  if (isLoading) {
    scrollToIframe();

    return <Skeleton />;
  }

  if (error) {
    const e = error?.response?.status;

    if (e !== 401) {
      return <GlobalErrorInfo retryHandler={refetch} />;
    }
  }

  return (
    <BaseLayout footer={Footer}>
      <PageBackButton
        variant="text"
        icon={<ArrowLeftIcon2 />}
        onClick={handleClickBack}
      >
        {t('COMMON:buttons.back')}
      </PageBackButton>
      <OrderCheckoutTitleWrapper>
        <Container>
          <OrderTitle isAdaptive={isMiteRoute}>
            {nonSubscriptionProducts.includes(selectedProduct || '')
              ? t('ORDER:headers.policyPayment')
              : t('ORDER:headers.subscriptionPayment')}
          </OrderTitle>
        </Container>
      </OrderCheckoutTitleWrapper>
      <OrderWrapper>
        {orderResult?.map((orderItem) => (
          <OrderCheckoutItem
            key={`${orderItem.title} + ${orderItem.value}`}
            isColsIdentical={true}
            {...orderItem}
          />
        ))}
      </OrderWrapper>
      <Container>
        <OrderWrapperTitle isAdaptive={isMiteRoute}>
          {t('ORDER:headers.documentsTitle')}
        </OrderWrapperTitle>
        {isTelevetPlusRoute && (
          <DocumentsSubTitle>{t('ORDER:labels.canDownload')}</DocumentsSubTitle>
        )}
      </Container>
      <DocumentsWrapper>
        {clientDocuments?.map((clientDocumentItem) => (
          <ClientDocumentItem key={uuid()} {...clientDocumentItem} />
        ))}
      </DocumentsWrapper>
      <OrderCheckoutAggrWrapper>
        <Controller
          control={control}
          name="insurancePolicies"
          render={({ field: { onChange }, fieldState }) => (
            <Checkbox
              label={acceptTermsLabel}
              name="insurancePolicies"
              onChange={onChange}
              message={errors.insurancePolicies?.message}
              status={fieldState.error && 'error'}
            />
          )}
        />
      </OrderCheckoutAggrWrapper>
    </BaseLayout>
  );
};
